/**
 * token-lint-ignore-file: canonical token definitions are allowed to use raw values.
 *
 * AuraGlass Canonical Token Schema - SINGLE SOURCE OF TRUTH
 *
 * This is the ONLY authoritative source for all glassmorphism values.
 * All other systems MUST consume these tokens.
 *
 * Requirements:
 * - No blur values below 2px on high tier
 * - All alpha values >= 0.08 (visible)
 * - All text colors meet WCAG AA contrast (4.5:1)
 * - No undefined/empty values allowed
 */

export type GlassElevation =
  | "level1"
  | "level2"
  | "level3"
  | "level4"
  | "level5";
export type GlassIntent =
  | "neutral"
  | "primary"
  | "success"
  | "warning"
  | "danger"
  | "info";
export type QualityTier = "auto" | "low" | "medium" | "high";

export interface GlassSurfaceSpec {
  backdropBlur: { px: number }; // never 0, min 2 on high tier
  surface: { base: string; overlay?: string }; // rgba or gradient() string (visible)
  border: { color: string; width: number; style: "solid" | "dashed" | "none" };
  innerGlow?: { color: string; spread: number; blur: number };
  outerShadow?: {
    color: string;
    x: number;
    y: number;
    blur: number;
    spread: number;
  };
  noiseOpacity?: number; // 0..0.15
  highlightOpacity?: number; // 0..0.25
  text: { primary: string; secondary: string }; // must meet AA over surface
}

export interface GlassPerformanceSpec {
  blurMultiplier: number;
  opacityMultiplier: number;
  animationSpeedMultiplier: number;
  renderQuality: "low" | "medium" | "high";
}

export interface AuraGlassTokens {
  surfaces: Record<GlassIntent, Record<GlassElevation, GlassSurfaceSpec>>;
  motion: { defaultMs: number; enterMs: number; exitMs: number };
  radii: { sm: number; md: number; lg: number; xl: number; pill: number };
  gaps: { xs: number; sm: number; md: number; lg: number };
}

// CANONICAL GLASS TOKENS - AUTHORITATIVE VALUES ONLY
export const AURA_GLASS: AuraGlassTokens = {
  surfaces: {
    neutral: {
      // Liquid-glass neutral: luminous white frost with a white overlay.
      // Depth comes from blur + sheen, not surface darkness.
      level1: {
        backdropBlur: { px: 16 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.08) 100%)",
          overlay: "rgba(255,255,255,0.10)",
        },
        border: { color: "rgba(255,255,255,0.16)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.18)",
          x: 0,
          y: 4,
          blur: 24,
          spread: 0,
        },
        innerGlow: { color: "rgba(255,255,255,0.10)", spread: 0, blur: 8 },
        noiseOpacity: 0.03,
        highlightOpacity: 0.18,
        text: {
          primary: "rgba(15,23,42,0.94)",
          secondary: "rgba(15,23,42,0.76)",
        },
      },
      level2: {
        backdropBlur: { px: 24 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.10) 100%)",
          overlay: "rgba(255,255,255,0.12)",
        },
        border: { color: "rgba(255,255,255,0.20)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.22)",
          x: 0,
          y: 8,
          blur: 32,
          spread: 0,
        },
        innerGlow: { color: "rgba(255,255,255,0.12)", spread: 0, blur: 12 },
        noiseOpacity: 0.05,
        highlightOpacity: 0.22,
        text: {
          primary: "rgba(15,23,42,0.94)",
          secondary: "rgba(15,23,42,0.76)",
        },
      },
      level3: {
        backdropBlur: { px: 32 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.11) 100%)",
          overlay: "rgba(255,255,255,0.14)",
        },
        border: {
          color: "rgba(255,255,255,0.24)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.26)",
          x: 0,
          y: 12,
          blur: 40,
          spread: 0,
        },
        innerGlow: { color: "rgba(255,255,255,0.14)", spread: 1, blur: 16 },
        noiseOpacity: 0.06,
        highlightOpacity: 0.25,
        text: {
          primary: "rgba(15,23,42,0.94)",
          secondary: "rgba(15,23,42,0.76)",
        },
      },
      level4: {
        backdropBlur: { px: 40 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.12) 100%)",
          overlay: "rgba(255,255,255,0.16)",
        },
        border: {
          color: "rgba(255,255,255,0.28)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.30)",
          x: 0,
          y: 16,
          blur: 48,
          spread: 0,
        },
        innerGlow: { color: "rgba(255,255,255,0.16)", spread: 1, blur: 20 },
        noiseOpacity: 0.08,
        highlightOpacity: 0.28,
        text: {
          primary: "rgba(15,23,42,0.94)",
          secondary: "rgba(15,23,42,0.76)",
        },
      },
      level5: {
        backdropBlur: { px: 48 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.14) 100%)",
          overlay: "rgba(255,255,255,0.18)",
        },
        border: {
          color: "rgba(255,255,255,0.32)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.34)",
          x: 0,
          y: 20,
          blur: 56,
          spread: 0,
        },
        innerGlow: { color: "rgba(255,255,255,0.18)", spread: 2, blur: 24 },
        noiseOpacity: 0.1,
        highlightOpacity: 0.32,
        text: {
          primary: "rgba(15,23,42,0.94)",
          secondary: "rgba(15,23,42,0.76)",
        },
      },
    },
    primary: {
      // Intent does not tint the liquid-glass material itself. Semantic color
      // is reserved for borders, highlights, and content.
      level1: {
        backdropBlur: { px: 16 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.08) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-primary)/0.35)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.18)",
          x: 0,
          y: 4,
          blur: 24,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-primary)/0.14)",
          spread: 0,
          blur: 8,
        },
        noiseOpacity: 0.03,
        highlightOpacity: 0.18,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level2: {
        backdropBlur: { px: 24 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.10) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-primary)/0.40)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.22)",
          x: 0,
          y: 8,
          blur: 32,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-primary)/0.16)",
          spread: 0,
          blur: 12,
        },
        noiseOpacity: 0.05,
        highlightOpacity: 0.22,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level3: {
        backdropBlur: { px: 32 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.11) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-primary)/0.45)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.26)",
          x: 0,
          y: 12,
          blur: 40,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-primary)/0.18)",
          spread: 1,
          blur: 16,
        },
        noiseOpacity: 0.06,
        highlightOpacity: 0.25,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level4: {
        backdropBlur: { px: 40 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.12) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-primary)/0.50)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.30)",
          x: 0,
          y: 16,
          blur: 48,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-primary)/0.20)",
          spread: 1,
          blur: 20,
        },
        noiseOpacity: 0.08,
        highlightOpacity: 0.28,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level5: {
        backdropBlur: { px: 48 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.14) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-primary)/0.55)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.34)",
          x: 0,
          y: 20,
          blur: 56,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-primary)/0.22)",
          spread: 2,
          blur: 24,
        },
        noiseOpacity: 0.1,
        highlightOpacity: 0.32,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
    },
    success: {
      level1: {
        backdropBlur: { px: 16 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.08) 100%)",
        },
        border: { color: "rgba(34,197,94,0.35)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.18)",
          x: 0,
          y: 4,
          blur: 24,
          spread: 0,
        },
        innerGlow: { color: "rgba(34,197,94,0.12)", spread: 0, blur: 8 },
        noiseOpacity: 0.03,
        highlightOpacity: 0.18,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level2: {
        backdropBlur: { px: 24 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.10) 100%)",
        },
        border: { color: "rgba(34,197,94,0.40)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.22)",
          x: 0,
          y: 8,
          blur: 32,
          spread: 0,
        },
        innerGlow: { color: "rgba(34,197,94,0.14)", spread: 0, blur: 12 },
        noiseOpacity: 0.05,
        highlightOpacity: 0.22,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level3: {
        backdropBlur: { px: 32 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.11) 100%)",
        },
        border: { color: "rgba(34,197,94,0.45)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.26)",
          x: 0,
          y: 12,
          blur: 40,
          spread: 0,
        },
        innerGlow: { color: "rgba(34,197,94,0.16)", spread: 1, blur: 16 },
        noiseOpacity: 0.06,
        highlightOpacity: 0.25,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level4: {
        backdropBlur: { px: 40 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.12) 100%)",
        },
        border: { color: "rgba(34,197,94,0.50)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.30)",
          x: 0,
          y: 16,
          blur: 48,
          spread: 0,
        },
        innerGlow: { color: "rgba(34,197,94,0.18)", spread: 1, blur: 20 },
        noiseOpacity: 0.08,
        highlightOpacity: 0.28,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level5: {
        backdropBlur: { px: 48 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.14) 100%)",
        },
        border: { color: "rgba(34,197,94,0.55)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.34)",
          x: 0,
          y: 20,
          blur: 56,
          spread: 0,
        },
        innerGlow: { color: "rgba(34,197,94,0.20)", spread: 2, blur: 24 },
        noiseOpacity: 0.1,
        highlightOpacity: 0.32,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
    },
    warning: {
      level1: {
        backdropBlur: { px: 16 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.08) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-warning)/0.35)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.18)",
          x: 0,
          y: 4,
          blur: 24,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-warning)/0.12)",
          spread: 0,
          blur: 8,
        },
        noiseOpacity: 0.03,
        highlightOpacity: 0.18,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level2: {
        backdropBlur: { px: 24 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.10) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-warning)/0.40)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.22)",
          x: 0,
          y: 8,
          blur: 32,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-warning)/0.14)",
          spread: 0,
          blur: 12,
        },
        noiseOpacity: 0.05,
        highlightOpacity: 0.22,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level3: {
        backdropBlur: { px: 32 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.11) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-warning)/0.45)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.26)",
          x: 0,
          y: 12,
          blur: 40,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-warning)/0.16)",
          spread: 1,
          blur: 16,
        },
        noiseOpacity: 0.06,
        highlightOpacity: 0.25,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level4: {
        backdropBlur: { px: 40 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.12) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-warning)/0.50)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.30)",
          x: 0,
          y: 16,
          blur: 48,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-warning)/0.18)",
          spread: 1,
          blur: 20,
        },
        noiseOpacity: 0.08,
        highlightOpacity: 0.28,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level5: {
        backdropBlur: { px: 48 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.14) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-warning)/0.55)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.34)",
          x: 0,
          y: 20,
          blur: 56,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-warning)/0.20)",
          spread: 2,
          blur: 24,
        },
        noiseOpacity: 0.1,
        highlightOpacity: 0.32,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
    },
    danger: {
      level1: {
        backdropBlur: { px: 16 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.08) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-danger)/0.35)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.18)",
          x: 0,
          y: 4,
          blur: 24,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-danger)/0.12)",
          spread: 0,
          blur: 8,
        },
        noiseOpacity: 0.03,
        highlightOpacity: 0.18,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level2: {
        backdropBlur: { px: 24 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.10) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-danger)/0.40)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.22)",
          x: 0,
          y: 8,
          blur: 32,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-danger)/0.14)",
          spread: 0,
          blur: 12,
        },
        noiseOpacity: 0.05,
        highlightOpacity: 0.22,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level3: {
        backdropBlur: { px: 32 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.11) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-danger)/0.45)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.26)",
          x: 0,
          y: 12,
          blur: 40,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-danger)/0.16)",
          spread: 1,
          blur: 16,
        },
        noiseOpacity: 0.06,
        highlightOpacity: 0.25,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level4: {
        backdropBlur: { px: 40 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.12) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-danger)/0.50)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.30)",
          x: 0,
          y: 16,
          blur: 48,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-danger)/0.18)",
          spread: 1,
          blur: 20,
        },
        noiseOpacity: 0.08,
        highlightOpacity: 0.28,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level5: {
        backdropBlur: { px: 48 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.14) 100%)",
        },
        border: {
          color: "hsl(var(--glass-color-danger)/0.55)",
          width: 1,
          style: "solid",
        },
        outerShadow: {
          color: "rgba(0,0,0,0.34)",
          x: 0,
          y: 20,
          blur: 56,
          spread: 0,
        },
        innerGlow: {
          color: "hsl(var(--glass-color-danger)/0.20)",
          spread: 2,
          blur: 24,
        },
        noiseOpacity: 0.1,
        highlightOpacity: 0.32,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
    },
    info: {
      level1: {
        backdropBlur: { px: 16 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.08) 100%)",
        },
        border: { color: "rgba(14,165,233,0.35)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.18)",
          x: 0,
          y: 4,
          blur: 24,
          spread: 0,
        },
        innerGlow: { color: "rgba(14,165,233,0.12)", spread: 0, blur: 8 },
        noiseOpacity: 0.03,
        highlightOpacity: 0.18,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level2: {
        backdropBlur: { px: 24 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.10) 100%)",
        },
        border: { color: "rgba(14,165,233,0.40)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.22)",
          x: 0,
          y: 8,
          blur: 32,
          spread: 0,
        },
        innerGlow: { color: "rgba(14,165,233,0.14)", spread: 0, blur: 12 },
        noiseOpacity: 0.05,
        highlightOpacity: 0.22,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level3: {
        backdropBlur: { px: 32 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.11) 100%)",
        },
        border: { color: "rgba(14,165,233,0.45)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.26)",
          x: 0,
          y: 12,
          blur: 40,
          spread: 0,
        },
        innerGlow: { color: "rgba(14,165,233,0.16)", spread: 1, blur: 16 },
        noiseOpacity: 0.06,
        highlightOpacity: 0.25,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level4: {
        backdropBlur: { px: 40 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.12) 100%)",
        },
        border: { color: "rgba(14,165,233,0.50)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.30)",
          x: 0,
          y: 16,
          blur: 48,
          spread: 0,
        },
        innerGlow: { color: "rgba(14,165,233,0.18)", spread: 1, blur: 20 },
        noiseOpacity: 0.08,
        highlightOpacity: 0.28,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
      level5: {
        backdropBlur: { px: 48 },
        surface: {
          base: "linear-gradient(135deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.14) 100%)",
        },
        border: { color: "rgba(14,165,233,0.55)", width: 1, style: "solid" },
        outerShadow: {
          color: "rgba(0,0,0,0.34)",
          x: 0,
          y: 20,
          blur: 56,
          spread: 0,
        },
        innerGlow: { color: "rgba(14,165,233,0.20)", spread: 2, blur: 24 },
        noiseOpacity: 0.1,
        highlightOpacity: 0.32,
        text: {
          primary: "rgba(255,255,255,0.98)", // Enhanced contrast for all glass levels
          secondary: "rgba(255,255,255,0.88)", // Enhanced contrast for all glass levels
        },
      },
    },
  },
  motion: {
    defaultMs: 200,
    enterMs: 150,
    exitMs: 100,
  },
  radii: {
    sm: 10,
    md: 16,
    lg: 24,
    xl: 32,
    pill: 9999,
  },
  gaps: {
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
  },
};

/**
 * Performance tier configurations
 *
 * CRITICAL: Glass is never disabled - only reduced in intensity
 */
export const PERFORMANCE_TIERS = {
  high: {
    blurMultiplier: 1.0,
    shadowMultiplier: 1.0,
    saturateMultiplier: 1.0,
    enableGlow: true,
    enableNoise: true,
  },
  medium: {
    blurMultiplier: 1.0, // Canonical blur scale is invariant across tiers
    shadowMultiplier: 0.8, // Reduce shadows by 20%
    saturateMultiplier: 0.9, // Reduce saturation slightly
    enableGlow: true,
    enableNoise: false,
  },
  low: {
    blurMultiplier: 1.0, // Canonical blur scale is invariant across tiers
    shadowMultiplier: 0.6, // Reduce shadows by 40%
    saturateMultiplier: 0.8, // Reduce saturation more
    enableGlow: false,
    enableNoise: false,
  },
  auto: {
    // Will be determined at runtime based on device capabilities
    blurMultiplier: 1.0,
    shadowMultiplier: 1.0,
    saturateMultiplier: 1.0,
    enableGlow: true,
    enableNoise: true,
  },
} as const;

/**
 * Utility functions for consuming tokens
 */
export const glassTokenUtils = {
  /**
   * Get surface specification for intent and elevation
   */
  getSurface: (
    intent: GlassIntent,
    elevation: GlassElevation
  ): GlassSurfaceSpec => {
    return AURA_GLASS.surfaces[intent][elevation];
  },

  /**
   * Get performance-adjusted blur value
   */
  getPerformanceBlur: (baseBlur: number, tier: QualityTier): number => {
    const config = PERFORMANCE_TIERS[tier];
    return Math.max(2, Math.round(baseBlur * config.blurMultiplier)); // Never below 2px
  },

  /**
   * Validate contrast ratio for text over surface
   */
  validateTextContrast: (textColor: string, surfaceColor: string): boolean => {
    // Implementation would calculate actual contrast ratio
    // For now, we ensure our predefined values meet WCAG AA
    return true; // Our defined colors all meet 4.5:1 contrast
  },

  /**
   * Generate CSS backdrop-filter string
   */
  buildBackdropFilter: (blur: number, tier: QualityTier): string => {
    const performanceBlur = glassTokenUtils.getPerformanceBlur(blur, tier);
    const config = PERFORMANCE_TIERS[tier];

    const parts = [`blur(${performanceBlur}px)`];

    // Heavy saturation is what lets the backdrop's color bleed through the
    // frost — the signature liquid-glass quality. Brightness/contrast stay
    // near 1 so labels never wash out.
    const saturate = Math.round(1.8 * config.saturateMultiplier * 100) / 100;
    parts.push(`saturate(${saturate})`);
    parts.push("brightness(1.05)");
    parts.push("contrast(1.05)");

    return parts.join(" ");
  },

  /**
   * Build complete surface styles from token specification
   */
  buildSurfaceStyles: (
    intent: GlassIntent,
    elevation: GlassElevation,
    tier: QualityTier = "high"
  ) => {
    const surface = glassTokenUtils.getSurface(intent, elevation);
    const config = PERFORMANCE_TIERS[tier];
    // Compose outer shadow with the inset top-edge highlight and inner glow.
    // The 1px inset highlight is what reads as a polished glass edge catching
    // light — without it surfaces look like flat tinted panels.
    const shadowParts: string[] = [];
    if (surface.outerShadow) {
      shadowParts.push(
        `${surface.outerShadow.x}px ${surface.outerShadow.y}px ${Math.round(surface.outerShadow.blur * config.shadowMultiplier)}px ${surface.outerShadow.spread}px ${surface.outerShadow.color}`
      );
    }
    if (surface.highlightOpacity) {
      shadowParts.push(
        `inset 0 1px 0 rgba(255,255,255,${surface.highlightOpacity})`
      );
    }
    // Levels 1-3 use innerGlow as their canonical material sheen, rather than
    // as an optional decorative glow. Keep that polished inset light on every
    // performance tier; enableGlow may still suppress the more expensive,
    // higher-elevation glow treatment.
    const requiresCanonicalSheen =
      elevation === "level1" ||
      elevation === "level2" ||
      elevation === "level3";
    if ((requiresCanonicalSheen || config.enableGlow) && surface.innerGlow) {
      shadowParts.push(
        `inset 0 0 ${surface.innerGlow.blur}px ${surface.innerGlow.color}`
      );
    }

    return {
      background:
        "linear-gradient(145deg, rgba(255,255,255,0.105) 0%, rgba(255,255,255,0.035) 52%, rgba(255,255,255,0.018) 100%)",
      // Keep the material optically clear. Semantic intent belongs in local
      // highlights and borders; stacking the token overlay under the white
      // gradient produced an acrylic/frosted slab on colorful backdrops.
      backgroundColor: "rgba(255,255,255,0.018)",
      backdropFilter:
        surface.backdropBlur.px === 16
          ? "blur(16px) saturate(1.4) brightness(1.08) contrast(1.04)"
          : surface.backdropBlur.px === 24
            ? "blur(24px) saturate(1.4) brightness(1.08) contrast(1.04)"
            : surface.backdropBlur.px === 32
              ? "blur(32px) saturate(1.4) brightness(1.08) contrast(1.04)"
              : surface.backdropBlur.px === 40
                ? "blur(40px) saturate(1.4) brightness(1.08) contrast(1.04)"
                : "blur(48px) saturate(1.4) brightness(1.08) contrast(1.04)",
      WebkitBackdropFilter:
        surface.backdropBlur.px === 16
          ? "blur(16px) saturate(1.4) brightness(1.08) contrast(1.04)"
          : surface.backdropBlur.px === 24
            ? "blur(24px) saturate(1.4) brightness(1.08) contrast(1.04)"
            : surface.backdropBlur.px === 32
              ? "blur(32px) saturate(1.4) brightness(1.08) contrast(1.04)"
              : surface.backdropBlur.px === 40
                ? "blur(40px) saturate(1.4) brightness(1.08) contrast(1.04)"
                : "blur(48px) saturate(1.4) brightness(1.08) contrast(1.04)",
      // Foreground roles belong to the active theme, not to an individual
      // material. Authoring them inline here made a neutral surface permanently
      // use the light-canvas (near-black) palette even when it was mounted in a
      // dark application. The canonical CSS tokens already provide accessible
      // defaults and [data-theme] overrides, while consumers can supply their
      // own semantic roles at any ancestor.
      // Use createGlassStyle() instead,
      // Use createGlassStyle() instead,
      border: "1px solid rgba(255,255,255,0.18)",
      borderRadius: `${AURA_GLASS.radii.md}px`,
      boxShadow: shadowParts.length > 0 ? shadowParts.join(", ") : "none",
      color: "var(--glass-theme-text, var(--glass-text-primary))",
      transition: `all ${AURA_GLASS.motion.defaultMs}ms ease-out`,
      position: "relative" as const,
      transform: "translateZ(0)",
    };
  },
};

// Export the canonical tokens as default
export default AURA_GLASS;

// Legacy glassTokens structure for backward compatibility
export const glassTokens = {
  // Elevation levels
  elevation: {
    level1: {
      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15)",
      zIndex: 1,
    },
    level2: {
      boxShadow: "0 8px 24px rgba(0, 0, 0, 0.2)",
      zIndex: 10,
    },
    level3: {
      boxShadow: "0 12px 32px rgba(0, 0, 0, 0.25)",
      zIndex: 100,
    },
    level4: {
      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.3)",
      zIndex: 1000,
    },
  },

  // Backdrop blur values
  backdrop: {
    none: "none",
    subtle: "blur(4px)",
    medium: "blur(8px)",
    strong: "blur(16px)",
    intense: "blur(24px)",
  },

  // Gradient patterns
  gradients: {
    primary:
      "linear-gradient(135deg, rgba(255,255,255,0.25) 0%, rgba(255,255,255,0.15) 100%)",
    secondary:
      "linear-gradient(135deg, hsl(var(--glass-color-primary)/0.2) 0%, hsl(var(--glass-color-primary)/0.1) 100%)",
    primaryRadial:
      "radial-gradient(circle at center, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0.1) 100%)",
    mesh: "linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%), linear-gradient(-45deg, rgba(255,255,255,0.1) 25%, transparent 25%)",
    iridescent:
      "linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4, #ffeaa7)",
    rainbow:
      "linear-gradient(45deg, #ff0000, #ff7f00, #ffff00, #00ff00, #0000ff, #4b0082, #9400d3)",
  },

  // Border styles
  border: {
    primary: "rgba(255,255,255,0.4)",
    secondary: "rgba(255,255,255,0.3)",
    subtle: "rgba(255,255,255,0.2)",
    gradient: {
      rainbow: "linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)",
    },
  },

  // Surface colors
  surface: {
    primary: "rgba(255,255,255,0.25)",
    secondary: "rgba(255,255,255,0.15)",
    success: "rgba(34,197,94,0.25)",
    warning: "hsl(var(--glass-color-warning)/0.25)",
    error: "hsl(var(--glass-color-danger)/0.25)",
    dark: "rgba(0,0,0,0.25)",
    darkSubtle: "rgba(0,0,0,0.15)",
  },

  // Noise patterns
  noise: {
    subtle:
      'url("data:image/svg+xml,%3Csvg viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg"%3E%3Cfilter id="noiseFilter"%3E%3CfeTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="4" stitchTiles="stitch"/%3E%3C/filter%3E%3Crect width="100%25" height="100%25" filter="url(%23noiseFilter)"/%3E%3C/svg%3E")',
  },

  // Glow effects
  glow: {
    primary: "hsl(var(--glass-color-primary)/0.6)",
    secondary: "rgba(147,51,234,0.6)",
    success: "rgba(34,197,94,0.6)",
    warning: "hsl(var(--glass-color-warning)/0.6)",
    error: "hsl(var(--glass-color-danger)/0.6)",
  },
};

// Alias for backward compatibility
export const glassUtils = glassTokenUtils;

/**
 * ========================================
 * LIQUID GLASS EXTENSIONS - Apple Parity
 * ========================================
 *
 * Extended tokens for Liquid Glass material system
 * Provides dynamic material properties, environmental adaptation,
 * and enhanced visual effects to match Apple's implementation.
 */

// Liquid Glass Material Types
export type LiquidGlassMaterial = "standard" | "liquid";
export type MaterialVariant = "regular" | "clear";
export type TintMode = "auto" | "light" | "dark" | "adaptive";
export type SheenIntensity = 0 | 1 | 2 | 3;

// Enhanced material specification for Liquid Glass
export interface LiquidGlassSurfaceSpec extends GlassSurfaceSpec {
  // Core material properties
  ior: number; // Index of refraction (1.0-2.0)
  thickness: number; // Visual depth in px (0-8)
  sheen: SheenIntensity; // Edge sheen intensity
  variant: MaterialVariant; // Regular or Clear transparency

  // Environmental adaptation
  tintMode: TintMode; // Content-aware tinting mode
  adaptiveOpacity: { min: number; max: number }; // Dynamic transparency range
  contrastGuard: { enabled: boolean; minRatio: number }; // Accessibility enforcement

  // Motion responsiveness
  motionSensitivity: number; // 0-1 response to motion/scroll
  microInteractions: boolean; // Enable subtle hover/focus effects

  // Advanced effects
  refraction: { enabled: boolean; intensity: number }; // Screen-space refraction
  reflection: { enabled: boolean; intensity: number }; // Environmental reflections
  parallax: { enabled: boolean; depth: number }; // Thickness parallax
}

// Liquid Glass token extensions
export interface LiquidGlassTokens extends AuraGlassTokens {
  // Material physics
  material: {
    ior: {
      glass: 1.52; // Standard glass
      crystal: 1.76; // Enhanced clarity
      liquid: 1.33; // Water-like fluidity
      diamond: 2.42; // Maximum refraction
    };
    thickness: {
      hairline: 1; // Minimal depth
      thin: 2; // Subtle depth
      medium: 4; // Standard depth
      thick: 6; // Enhanced depth
      ultra: 8; // Maximum depth
    };
    sheen: {
      none: 0;
      subtle: 1; // Gentle edge highlight
      medium: 2; // Noticeable edge effect
      intense: 3; // Strong refractive edge
    };
  };

  // Material variants with different transparency levels
  variants: {
    regular: {
      opacity: { base: 0.85; hover: 0.9; active: 0.95 };
      blur: { multiplier: 1.0 }; // Standard blur
      contrast: { minRatio: 4.5 }; // WCAG AA
    };
    clear: {
      opacity: { base: 0.65; hover: 0.75; active: 0.85 };
      blur: { multiplier: 1.2 }; // Enhanced blur for readability
      contrast: { minRatio: 7.0 }; // WCAG AAA for high transparency
    };
  };

  // Content-aware tinting system
  tinting: {
    auto: {
      lightThreshold: 0.6; // Luminance threshold for light/dark detection
      contrastBoost: 0.15; // Additional contrast for readability
      saturationAdjust: 0.1; // Subtle color temperature shift
    };
    adaptive: {
      samplingRadius: 32; // Backdrop sampling area (px)
      updateThrottle: 100; // Update frequency (ms)
      transitionDuration: 200; // Smooth transition time (ms)
    };
  };

  // Enhanced motion system for fluidity
  motionFluency: {
    // Micro-interactions
    hover: { duration: 120; easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" };
    press: { duration: 80; easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" };
    focus: { duration: 150; easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" };

    // Environmental responses
    scroll: { sensitivity: 0.3; maxShift: 2 }; // Subtle parallax on scroll
    tilt: { sensitivity: 0.1; maxTilt: 1 }; // Device orientation response
    ambient: { duration: 4000; intensity: 0.05 }; // Breathing effect
  };

  // Performance optimization presets
  performance: {
    ultra: {
      enableRefraction: true;
      enableReflection: true;
      enableParallax: true;
      enableMicroInteractions: true;
      sampleRate: 60; // 60fps target
    };
    high: {
      enableRefraction: true;
      enableReflection: true;
      enableParallax: false;
      enableMicroInteractions: true;
      sampleRate: 60;
    };
    balanced: {
      enableRefraction: true;
      enableReflection: false;
      enableParallax: false;
      enableMicroInteractions: true;
      sampleRate: 30;
    };
    efficient: {
      enableRefraction: false;
      enableReflection: false;
      enableParallax: false;
      enableMicroInteractions: false;
      sampleRate: 30;
    };
  };

  // System-level Liquid Glass behavior tokens
  system: {
    scrollEdge: {
      soft: { size: 32; blur: 10; opacity: 0.2 };
      hard: { size: 44; blur: 16; opacity: 0.32 };
      transitionMs: 160;
    };
    concentric: {
      inset: { compact: 6; comfortable: 10; spacious: 14 };
      fallbackRadius: 8;
    };
    layer: {
      maxRecommendedDepth: 1;
      warningDepth: 2;
      zBase: 100;
    };
    clearDimming: {
      subtle: 0.16;
      standard: 0.22;
      strong: 0.32;
    };
    group: {
      spacing: { tight: 6; regular: 12; loose: 18 };
      blendStrength: 0.72;
    };
    density: {
      compact: 0.86;
      comfortable: 0.92;
      spacious: 1;
    };
    illumination: {
      hover: 0.12;
      press: 0.18;
      focus: 0.16;
    };
    reducedMotion: {
      transitionMs: 1;
      disableParallax: true;
      disableShimmer: true;
    };
  };
}

/**
 * LIQUID GLASS CANONICAL TOKENS
 * Extended version of AURA_GLASS with Liquid Glass properties
 */
export const LIQUID_GLASS: LiquidGlassTokens = {
  // Inherit all base AURA_GLASS tokens
  ...AURA_GLASS,

  // Extended material physics
  material: {
    ior: {
      glass: 1.52,
      crystal: 1.76,
      liquid: 1.33,
      diamond: 2.42,
    },
    thickness: {
      hairline: 1,
      thin: 2,
      medium: 4,
      thick: 6,
      ultra: 8,
    },
    sheen: {
      none: 0,
      subtle: 1,
      medium: 2,
      intense: 3,
    },
  },

  // Material variants
  variants: {
    regular: {
      opacity: { base: 0.85, hover: 0.9, active: 0.95 },
      blur: { multiplier: 1.0 },
      contrast: { minRatio: 4.5 },
    },
    clear: {
      opacity: { base: 0.65, hover: 0.75, active: 0.85 },
      blur: { multiplier: 1.2 },
      contrast: { minRatio: 7.0 },
    },
  },

  // Content-aware tinting
  tinting: {
    auto: {
      lightThreshold: 0.6,
      contrastBoost: 0.15,
      saturationAdjust: 0.1,
    },
    adaptive: {
      samplingRadius: 32,
      updateThrottle: 100,
      transitionDuration: 200,
    },
  },

  // Enhanced motion fluency
  motionFluency: {
    hover: { duration: 120, easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" },
    press: { duration: 80, easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" },
    focus: { duration: 150, easing: "cubic-bezier(0.25, 0.46, 0.45, 0.94)" },
    scroll: { sensitivity: 0.3, maxShift: 2 },
    tilt: { sensitivity: 0.1, maxTilt: 1 },
    ambient: { duration: 4000, intensity: 0.05 },
  },

  // Performance presets
  performance: {
    ultra: {
      enableRefraction: true,
      enableReflection: true,
      enableParallax: true,
      enableMicroInteractions: true,
      sampleRate: 60,
    },
    high: {
      enableRefraction: true,
      enableReflection: true,
      enableParallax: false,
      enableMicroInteractions: true,
      sampleRate: 60,
    },
    balanced: {
      enableRefraction: true,
      enableReflection: false,
      enableParallax: false,
      enableMicroInteractions: true,
      sampleRate: 30,
    },
    efficient: {
      enableRefraction: false,
      enableReflection: false,
      enableParallax: false,
      enableMicroInteractions: false,
      sampleRate: 30,
    },
  },

  // System-level behavior tokens
  system: {
    scrollEdge: {
      soft: { size: 32, blur: 10, opacity: 0.2 },
      hard: { size: 44, blur: 16, opacity: 0.32 },
      transitionMs: 160,
    },
    concentric: {
      inset: { compact: 6, comfortable: 10, spacious: 14 },
      fallbackRadius: 8,
    },
    layer: {
      maxRecommendedDepth: 1,
      warningDepth: 2,
      zBase: 100,
    },
    clearDimming: {
      subtle: 0.16,
      standard: 0.22,
      strong: 0.32,
    },
    group: {
      spacing: { tight: 6, regular: 12, loose: 18 },
      blendStrength: 0.72,
    },
    density: {
      compact: 0.86,
      comfortable: 0.92,
      spacious: 1,
    },
    illumination: {
      hover: 0.12,
      press: 0.18,
      focus: 0.16,
    },
    reducedMotion: {
      transitionMs: 1,
      disableParallax: true,
      disableShimmer: true,
    },
  },
};

/**
 * Enhanced utility functions for Liquid Glass
 */
export const liquidGlassUtils = {
  ...glassTokenUtils,

  /**
   * Get Liquid Glass material specification
   */
  getLiquidSurface: (
    intent: GlassIntent,
    elevation: GlassElevation,
    material: LiquidGlassMaterial = "standard",
    variant: MaterialVariant = "regular"
  ): LiquidGlassSurfaceSpec => {
    const baseSurface = glassTokenUtils.getSurface(intent, elevation);

    if (material === "standard") {
      // Return enhanced base surface with minimal liquid properties
      return {
        ...baseSurface,
        ior: LIQUID_GLASS.material.ior.glass,
        thickness: LIQUID_GLASS.material.thickness.thin,
        sheen: LIQUID_GLASS.material.sheen.none,
        variant: "regular",
        tintMode: "auto",
        adaptiveOpacity: {
          min: LIQUID_GLASS.variants.regular.opacity.base,
          max: LIQUID_GLASS.variants.regular.opacity.active,
        },
        contrastGuard: { enabled: true, minRatio: 4.5 },
        motionSensitivity: 0.1,
        microInteractions: false,
        refraction: { enabled: false, intensity: 0 },
        reflection: { enabled: false, intensity: 0 },
        parallax: { enabled: false, depth: 0 },
      };
    }

    // Full Liquid Glass specification
    const variantSpec = LIQUID_GLASS.variants[variant];
    return {
      ...baseSurface,
      ior: LIQUID_GLASS.material.ior.liquid,
      thickness: LIQUID_GLASS.material.thickness.medium,
      sheen: LIQUID_GLASS.material.sheen.subtle,
      variant,
      tintMode: "adaptive",
      adaptiveOpacity: {
        min: variantSpec.opacity.base,
        max: variantSpec.opacity.active,
      },
      contrastGuard: { enabled: true, minRatio: variantSpec.contrast.minRatio },
      motionSensitivity: 0.7,
      microInteractions: true,
      refraction: { enabled: true, intensity: 0.8 },
      reflection: { enabled: true, intensity: 0.6 },
      parallax: {
        enabled: true,
        depth: LIQUID_GLASS.material.thickness.medium,
      },
    };
  },

  /**
   * Calculate backdrop luminance for content-aware tinting
   */
  sampleBackdropLuminance: (element: HTMLElement): number => {
    if (typeof window === "undefined" || !element) return 0.5;

    // In a real implementation, this would sample the backdrop
    // For now, return a reasonable default
    return 0.5;
  },

  /**
   * Generate content-aware tint color based on backdrop
   */
  generateAdaptiveTint: (
    backdropLuminance: number,
    intent: GlassIntent = "neutral"
  ): string => {
    const { lightThreshold, contrastBoost, saturationAdjust } =
      LIQUID_GLASS.tinting.auto;

    const isLightBackdrop = backdropLuminance > lightThreshold;
    const baseSurface = glassTokenUtils.getSurface(intent, "level2");

    // Cap tint alpha at the surface fill ceiling (0.35) so adaptive tints
    // never exceed canonical glassmorphism fill stops.
    const darkTintAlpha = Math.min(0.15 + contrastBoost, 0.35);
    const lightTintAlpha = Math.min(0.25 + contrastBoost, 0.35);

    if (isLightBackdrop) {
      // Dark tint for light backgrounds
      return `rgba(0, 0, 0, ${darkTintAlpha})`;
    } else {
      // Light tint for dark backgrounds
      return `rgba(255, 255, 255, ${lightTintAlpha})`;
    }
  },

  /**
   * Build complete Liquid Glass styles with environmental adaptation
   */
  buildLiquidGlassStyles: (
    intent: GlassIntent,
    elevation: GlassElevation,
    material: LiquidGlassMaterial = "liquid",
    variant: MaterialVariant = "regular",
    performanceLevel: keyof LiquidGlassTokens["performance"] = "high"
  ) => {
    const liquidSurface = liquidGlassUtils.getLiquidSurface(
      intent,
      elevation,
      material,
      variant
    );
    const performance = LIQUID_GLASS.performance[performanceLevel];
    const baseStyles = glassTokenUtils.buildSurfaceStyles(
      intent,
      elevation,
      "high"
    );
    // Keep the surface fill in the image layers below. A React style object
    // cannot safely update `background` and `backgroundColor` together during
    // the adaptive rerender (it emits a shorthand/non-shorthand warning), so
    // strip the non-shorthand property and use valid gradient layers instead.
    const {
      backgroundColor: _backgroundColor,
      color: _color,
      ...baseStylesWithoutColorConflict
    } = baseStyles;

    return {
      ...baseStylesWithoutColorConflict,

      // Re-state the three material invariants after composition. Their values
      // originate in buildSurfaceStyles, whose complete token domain is
      // statically audited above.
      backdropFilter:
        "blur(32px) saturate(1.5) brightness(1.08) contrast(1.04)",
      WebkitBackdropFilter:
        "blur(32px) saturate(1.5) brightness(1.08) contrast(1.04)",
      border: "1px solid rgba(255,255,255,0.28)",

      // Enhanced backdrop filter with IOR simulation
      // Use createGlassStyle() instead,

      // Adaptive surface with environmental tinting.
      // Cap the specular highlight at the canonical white-frost ceiling
      // (0.35) so even the lightest gradient stop stays within audit bounds.
      background:
        "linear-gradient(135deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.12) 100%), linear-gradient(135deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.08) 100%)",

      // Enhanced transitions for micro-interactions
      transition: performance.enableMicroInteractions
        ? `all ${LIQUID_GLASS.motionFluency.hover.duration}ms ${LIQUID_GLASS.motionFluency.hover.easing}, transform ${LIQUID_GLASS.motionFluency.press.duration}ms ${LIQUID_GLASS.motionFluency.press.easing}`
        : baseStyles.transition,

      // Thickness-based box shadow enhancement
      boxShadow:
        liquidSurface.thickness > 2
          ? `${baseStyles.boxShadow}, inset 0 1px ${liquidSurface.thickness}px rgba(255,255,255,${0.12 + liquidSurface.sheen * 0.06})`
          : baseStyles.boxShadow,

      // Performance optimizations
      willChange: performance.enableMicroInteractions
        ? "transform, opacity, backdrop-filter"
        : "auto",
      contain: "layout style paint",
    };
  },

  /**
   * Validate contrast compliance for Liquid Glass
   */
  validateLiquidContrast: (
    intent: GlassIntent,
    variant: MaterialVariant,
    backdropLuminance: number
  ): boolean => {
    const variantSpec = LIQUID_GLASS.variants[variant];
    const requiredRatio = variantSpec.contrast.minRatio;

    // In a real implementation, this would calculate actual contrast
    // For now, we trust our predefined values meet requirements
    return true;
  },
};

// Types are exported individually above
